import { useState } from "react";
import { formatNumberInMillions } from "../../helper/constant";

export default function DonutChart({ values, primaryText, secondaryText }) {
  const baseStroke = 60;
  const hoverExtra = 10;
  const hoverStroke = baseStroke + hoverExtra;
  const gapSize = 4; // gap in pixels along circumference

  const size = 320 + hoverExtra * 2;
  const radius = (size - hoverStroke) / 2;
  const circumference = 2 * Math.PI * radius;

  const COLORS = ["#B4D3F3", "#6FA7E0", "#264C7A", "#638EBA"];
  const total = values.reduce((a, b) => a + b, 0);

  const [hoverIndex, setHoverIndex] = useState(null);

  let offset = 0;

  const dynamicColor = (index) => COLORS[index % COLORS.length];

  const renderLegend = (item, index) => {
    const backgroundColor = COLORS[index];
    const { name, value } = item;
    return (
      <div
        className={`d-flex align-items-start donut-legend gap-2`}
        key={`legend-${index}`}
      >
        <div className="donut-legend-item" style={{ backgroundColor }}></div>
        <div className="d-flex gap-1 flex-column align-items-start">
          <p className="m-0 p-0 text-uppercase label">{name}</p>
          <p className="m-0 p-0 text-uppercase value font-weight-bold">
            ${formatNumberInMillions(value)}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="d-flex align-items-center justify-content-center gap-2 ">
      {
        (values && values.length) && <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ cursor: "pointer", overflow: "visible" }}
        >
          {/* Background ring */}
          <circle
            r={radius}
            cx={size / 2}
            cy={size / 2}
            fill="transparent"
            stroke="#f3f4f6"
            strokeWidth={baseStroke}
          />

          {/* Donut slices */}
          {values.map((val, i) => {
            const isHovered = hoverIndex === i;
            const sliceLength = (val / total) * circumference - gapSize;
            const dashArray = `${sliceLength} ${circumference - sliceLength}`;
            const strokeWidth = isHovered ? hoverStroke : baseStroke;

            const circle = (
              <circle
                key={i}
                r={radius}
                cx={size / 2}
                cy={size / 2}
                fill="transparent"
                stroke={isHovered ? darken(COLORS[i], 0.15) : COLORS[i]}
                strokeWidth={strokeWidth}
                strokeDasharray={dashArray}
                strokeDashoffset={-offset}
                strokeLinecap="butt" // keeps gap shape clean
                onMouseEnter={() => setHoverIndex(i)}
                onMouseLeave={() => setHoverIndex(null)}
                style={{ transition: "all 0.25s ease" }}
              />
            );

            offset += (val / total) * circumference;
            return circle;
          })}

          {/* Center text */}
          <text
            x="50%"
            y="45%"
            textAnchor="middle"
            fill="#6b7280"
            fontSize="18"
            fontWeight="bold"
          >
            Additional Profit*
          </text>

          <text
            x="50%"
            y="53%"
            textAnchor="middle"
            fill="#3B82F6"
            fontSize="28"
            fontWeight="bold"
          >
            {primaryText ? `$${formatNumberInMillions(primaryText)}` : '--'}
          </text>

          <text
            x="50%"
            y="61%"
            textAnchor="middle"
            fill="#3B82F6"
            fontSize="20"
            fontWeight="bold"
          >
            {secondaryText ? `(${secondaryText}%)` : '--'}
          </text>
        </svg>
      }
      <div className="d-flex flex-column gap-2">
        {renderLegend({ name: 'revenue Increase', value: values[0] }, 0)}
        {renderLegend({ name: 'material cost reduction', value: values[1] }, 1)}
        {renderLegend({ name: 'manpower efficiencies', value: values[2] }, 2)}
      </div>
    </div>
  );
}

// Utility to darken a hex color
function darken(hex, amount) {
  const num = parseInt(hex.slice(1), 16);
  let r = (num >> 16) - amount * 255;
  let g = ((num >> 8) & 0x00ff) - amount * 255;
  let b = (num & 0x0000ff) - amount * 255;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return `rgb(${r}, ${g}, ${b})`;
}
